import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type Client, type Retainer, type Transaction } from '../../lib/supabase';
import CaseStatusTracker from './components/CaseStatusTracker';
import DocumentUpload from './components/DocumentUpload';
import SigningOverlay from './components/SigningOverlay';
import CaseNotes from './components/CaseNotes';
import Form4811 from './components/Form4811';
import Form5291 from './components/Form5291';
import TariffRefundEstimator from './components/TariffRefundEstimator';
import PreFilingChecklist from './components/PreFilingChecklist';
import CBPDocumentGathering from './components/CBPDocumentGathering';
import RetainerStatement from './components/RetainerStatement';
import ClientMessages from './components/ClientMessages';
import NotificationsPanel from './components/NotificationsPanel';
import { SUPPORTED_LANGUAGES, getLangInfo } from '../../lib/translate';
import AttorneyDocuments from './components/AttorneyDocuments';
import LogoBrand from '../../components/feature/LogoBrand';
import ProfileCompletion from './components/ProfileCompletion';

interface Appointment {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  start_time: string;
  end_time: string;
  notes: string;
  status: string;
  created_at: string;
}

interface CaseDocument {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  description: string | null;
  uploaded_at: string;
}

interface CaseNote {
  id: string;
  note: string;
  note_type: string;
  created_by: string;
  created_at: string;
}

interface ExtendedClient extends Client {
  admin_authorized?: boolean;
  engagement_signed_at?: string | null;
  engagement_signature_ip?: string | null;
  engagement_signature_name?: string | null;
  engagement_letter_url?: string | null;
  form_4811_submitted_at?: string | null;
  form_5297_submitted_at?: string | null;
  importer_of_record_name?: string | null;
  importer_of_record_address?: string | null;
  importer_of_record_phone?: string | null;
  liquidation_date?: string | null;
  protest_file_date?: string | null;
  client_notes?: string | null;
}

export default function PortalPage() {
  const navigate = useNavigate();
  const [client, setClient] = useState<ExtendedClient | null>(null);
  const [retainer, setRetainer] = useState<Retainer | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [appointmentsTab, setAppointmentsTab] = useState<'upcoming' | 'past'>('upcoming');
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [caseNotes, setCaseNotes] = useState<CaseNote[]>([]);
  const [userId, setUserId] = useState<string>('');
  const [engagementTemplateUrl, setEngagementTemplateUrl] = useState('');
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [form5297SubmittedAt, setForm5297SubmittedAt] = useState<string | null>(null);
  const [form4811SubmittedAt, setForm4811SubmittedAt] = useState<string | null>(null);
  const [form5291SubmittedAt, setForm5291SubmittedAt] = useState<string | null>(null);
  const [cbpDocsSubmittedAt, setCbpDocsSubmittedAt] = useState<string | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadAttorneyDocCount, setUnreadAttorneyDocCount] = useState(0);
  const [justAuthorized, setJustAuthorized] = useState(false);
  const [authToastVisible, setAuthToastVisible] = useState(false);
  // Ref to track previous authorization state — avoids stale closure in realtime subscription
  const prevAuthorizedRef = useRef<boolean>(false);
  // Pre-fill Importer of Record company name before opening modal
  const [importerOfRecordPrefill, setImporterOfRecordPrefill] = useState('');
  const [showFundRetainerPrompt, setShowFundRetainerPrompt] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const form5297Ref = useRef<HTMLDivElement>(null);
  const cbpDocsRef = useRef<HTMLDivElement>(null);
  const attorneyDocsRef = useRef<HTMLDivElement>(null);
  const fundRetainerRef = useRef<HTMLDivElement>(null);

  // Play a subtle chime when authorization fires
  const playAuthChime = () => {
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 — major chord arpeggio
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + i * 0.12 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.5);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.6);
      });
    } catch {
      // Audio not available — silent fallback
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadClientData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { navigate('/login'); return; }
        setUserId(user.id);

        const { data: clientData, error: clientError } = await supabase
          .from('clients').select('*').eq('user_id', user.id).maybeSingle();
        if (clientError) throw clientError;
        if (!clientData) { navigate('/login'); return; }

        setClient(clientData as ExtendedClient);
        // Seed the ref with the initial authorization state
        prevAuthorizedRef.current = !!(clientData as ExtendedClient).admin_authorized;

        // Sync email_confirmed_at — if user reached portal, email is confirmed
        supabase.from('clients').update({ email_confirmed_at: new Date().toISOString() })
          .eq('id', clientData.id).is('email_confirmed_at', null).then(() => {});

        const extDataAny = clientData as Record<string, unknown>;
        if (extDataAny.preferred_language && typeof extDataAny.preferred_language === 'string') {
          setPreferredLanguage(extDataAny.preferred_language);
        }
        const extData = clientData as ExtendedClient;
        // Track each form's submission independently
        if (extData.form_4811_submitted_at) {
          setForm4811SubmittedAt(extData.form_4811_submitted_at);
          setForm5297SubmittedAt(extData.form_4811_submitted_at);
        } else if (extData.form_5297_submitted_at) {
          setForm5297SubmittedAt(extData.form_5297_submitted_at);
        }
        const extDataAny2 = clientData as Record<string, unknown>;
        if (extDataAny2.form_5291_submitted_at) {
          setForm5291SubmittedAt(extDataAny2.form_5291_submitted_at as string);
        }
        if (extDataAny.cbp_docs_submitted_at) setCbpDocsSubmittedAt(extDataAny.cbp_docs_submitted_at as string);

        const { data: retainerData, error: retainerError } = await supabase
          .from('retainers').select('*').eq('client_id', clientData.id).maybeSingle();
        if (retainerError && retainerError.code !== 'PGRST116') throw retainerError;

        if (!retainerData) {
          const { data: newRetainer, error: createError } = await supabase
            .from('retainers')
            .insert({ client_id: clientData.id, balance: 0, total_funded: 0, total_used: 0 })
            .select().single();
          if (createError) throw createError;
          setRetainer(newRetainer);
        } else {
          setRetainer(retainerData);
        }

        const { data: transactionsData } = await supabase
          .from('transactions').select('*').eq('client_id', clientData.id)
          .order('created_at', { ascending: false }).limit(20);
        setTransactions(transactionsData || []);

        const { data: docsData } = await supabase
          .from('case_documents').select('*').eq('client_id', clientData.id)
          .order('uploaded_at', { ascending: false });
        setDocuments((docsData as CaseDocument[]) || []);

        const { data: notesData } = await supabase
          .from('case_notes').select('*').eq('client_id', clientData.id)
          .eq('is_visible', true).order('created_at', { ascending: false });
        setCaseNotes((notesData as CaseNote[]) || []);

        const { data: settingData } = await supabase
          .from('app_settings').select('value')
          .eq('key', 'engagement_letter_template_url').maybeSingle();
        if (settingData?.value) {
          setEngagementTemplateUrl(settingData.value + `?cb=${Date.now()}`);
        }

        // appointments loaded separately
        setAppointmentsLoading(false);
      } catch (error) {
        console.error('Error loading client data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadClientData();
  }, [navigate]);

  useEffect(() => {
    if (!client?.id) return;
    const clientId = client.id;
    const channel = supabase
      .channel(`client-auth-${clientId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'clients',
        filter: `id=eq.${clientId}`,
      }, async (payload) => {
        const updated = payload.new as Record<string, unknown>;
        // Always reload fresh client data on any update to this client row
        const { data: freshClient } = await supabase
          .from('clients').select('*').eq('id', clientId).maybeSingle();
        if (freshClient) {
          const wasNotAuthorized = !prevAuthorizedRef.current;
          const isNowAuthorized = !!(updated.admin_authorized);
          // Update the ref BEFORE setting state
          prevAuthorizedRef.current = isNowAuthorized;
          setClient(freshClient as ExtendedClient);
          // Fire the authorization celebration only when transitioning false → true
          if (wasNotAuthorized && isNowAuthorized) {
            setJustAuthorized(true);
            setAuthToastVisible(true);
            playAuthChime();
            setTimeout(() => setAuthToastVisible(false), 6000);
            // Auto-open the signing modal — client is live on the page, no refresh needed
            setTimeout(() => setShowEngagementModal(true), 1200);
          }
        }
      }).subscribe();
    return () => { supabase.removeChannel(channel); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  const handleLanguageChange = async (lang: string) => {
    setPreferredLanguage(lang);
    setLangDropdownOpen(false);
    if (client?.id) {
      await supabase.from('clients').update({ preferred_language: lang }).eq('id', client.id);
    }
    if (lang === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
    } else {
      document.cookie = `googtrans=/en/${lang}; path=/`;
      document.cookie = `googtrans=/en/${lang}; path=/; domain=${window.location.hostname}`;
    }
    window.location.reload();
  };

  const loadAppointments = useCallback(async (_email: string) => {
    setAppointmentsLoading(false);
    setAppointments([]);
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleFundRetainer = () => navigate('/portal/fund');

  const handleEngagementSigned = (data: { letterUrl: string; signedAt: string }) => {
    setClient((prev) => prev ? { ...prev, engagement_signed_at: data.signedAt, engagement_letter_url: data.letterUrl } : prev);
    setImporterOfRecordPrefill('');
    setShowFundRetainerPrompt(true);
  };

  const handleForm5297Submitted = (submittedAt: string) => setForm5297SubmittedAt(submittedAt);
  const handleForm4811Submitted = (submittedAt: string) => {
    setForm4811SubmittedAt(submittedAt);
    setForm5297SubmittedAt(submittedAt);
  };
  const handleForm5291Submitted = (submittedAt: string) => setForm5291SubmittedAt(submittedAt);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
    });

  const formatAppointmentDate = (datetime: string) =>
    new Date(datetime.replace(' ', 'T')).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

  const formatAppointmentTime = (datetime: string) =>
    new Date(datetime.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  const isUpcoming = (datetime: string) => new Date(datetime.replace(' ', 'T')) >= new Date();

  const upcomingAppointments = appointments.filter(apt => isUpcoming(apt.start_time));
  const pastAppointments = appointments.filter(apt => !isUpcoming(apt.start_time));

  // Scroll-to-top visibility
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'fund': return 'ri-arrow-down-circle-fill text-green-400';
      case 'debit': return 'ri-arrow-up-circle-fill text-red-400';
      case 'refund': return 'ri-refund-2-fill text-blue-400';
      default: return 'ri-exchange-line text-white/60';
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/40',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      failed: 'bg-red-500/20 text-red-400 border-red-500/40',
      refunded: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
      confirmed: 'bg-green-500/20 text-green-400 border-green-500/40',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/40',
    };
    return styles[status] || styles.confirmed;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0d1520] to-[#111827] flex items-center justify-center">
        <div className="text-white text-xl font-bold">Loading...</div>
      </div>
    );
  }

  const balance = retainer?.balance || 0;
  const totalFunded = retainer?.total_funded || 0;
  const totalUsed = retainer?.total_used || 0;
  const isLowBalance = balance < 500 && balance > 0;
  const caseStatus = (client as unknown as { case_status?: number })?.case_status ?? 0;
  const eligibilityAuthorized = !!(client as unknown as { eligibility_authorized?: boolean })?.eligibility_authorized;
  const extClient = client as ExtendedClient;
  const isAuthorized = extClient?.admin_authorized === true;
  const isSigned = !!extClient?.engagement_signed_at;
  // Modal opens whenever authorized + not signed — regardless of template URL
  const needsSignature = isAuthorized && !isSigned;
  const isProfileComplete = !!(
    extClient?.importer_of_record_name &&
    extClient?.importer_of_record_address &&
    extClient?.importer_of_record_phone
  );
  const currentLang = getLangInfo(preferredLanguage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d1520] to-[#111827]">

      {/* ── Scroll-to-top button ── */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-6 z-50 w-12 h-12 flex items-center justify-center bg-[#FDB913] hover:bg-[#e6a800] text-black rounded-full shadow-2xl transition-all cursor-pointer"
          aria-label="Scroll to top"
          style={{ boxShadow: '0 4px 24px rgba(253,185,19,0.4)' }}
        >
          <i className="ri-arrow-up-line text-xl font-black"></i>
        </button>
      )}

      {/* ── Authorization Toast — slides in from top ── */}
      {authToastVisible && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-4 bg-[#0d1520] border-2 border-green-500/70 rounded-2xl px-6 py-4 shadow-2xl"
          style={{
            animation: 'slideDownFade 0.4s cubic-bezier(0.34,1.56,0.64,1) both',
            boxShadow: '0 0 40px rgba(34,197,94,0.35)',
            minWidth: 340,
          }}
        >
          <style>{`
            @keyframes slideDownFade {
              from { opacity: 0; transform: translateX(-50%) translateY(-24px) scale(0.95); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1);    }
            }
          `}</style>
          <div className="w-12 h-12 flex items-center justify-center bg-green-500/25 border-2 border-green-500/60 rounded-xl flex-shrink-0">
            <i className="ri-shield-check-fill text-green-400 text-2xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-sm leading-tight">Account Unlocked!</p>
            <p className="text-green-400 text-xs mt-0.5">Your attorney just authorized your portal access.</p>
          </div>
          <button
            onClick={() => setAuthToastVisible(false)}
            className="text-white/30 hover:text-white transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
      )}

      {/* ── Signing Overlay — opens regardless of template URL ── */}
      {showEngagementModal && client && (
        <SigningOverlay
          clientId={client.id}
          clientName={client.name}
          clientEmail={client.email}
          company={client.company || ''}
          templateUrl={engagementTemplateUrl}
          initialImporterOfRecord={importerOfRecordPrefill || client.company || ''}
          onSigned={handleEngagementSigned}
          onClose={() => setShowEngagementModal(false)}
        />
      )}

      <header className="bg-black/50 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <LogoBrand size="md" linkTo="/" />
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="relative" ref={langDropdownRef}>
              <button
                onClick={() => setLangDropdownOpen((o) => !o)}
                className="flex items-center gap-2 bg-[#FDB913]/15 hover:bg-[#FDB913]/25 border-2 border-[#FDB913]/60 text-[#FDB913] font-black px-4 py-2 rounded-xl transition-all whitespace-nowrap cursor-pointer"
                title="Translate portal to your language"
              >
                <i className="ri-translate-2 text-base"></i>
                <span className="hidden sm:inline text-xs font-black tracking-wide">TRANSLATE</span>
                <span className="text-base leading-none">{currentLang.flag}</span>
                <i className={`ri-arrow-down-s-line text-[#FDB913] text-sm transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-[#0d1520] border-2 border-[#FDB913]/50 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="px-3 py-2 border-b border-[#FDB913]/20 bg-[#FDB913]/10">
                    <p className="text-[#FDB913] text-xs font-black tracking-widest uppercase flex items-center gap-2">
                      <i className="ri-global-line"></i> Portal Language
                    </p>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/10 transition-colors cursor-pointer ${
                          preferredLanguage === lang.code ? 'bg-[#FDB913]/10 text-[#FDB913]' : 'text-white/80'
                        }`}
                      >
                        <span className="text-base leading-none">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                        {preferredLanguage === lang.code && (
                          <i className="ri-check-line text-[#FDB913] ml-auto text-sm"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Attorney Documents Bell */}
            {unreadAttorneyDocCount > 0 && (
              <button
                onClick={() => attorneyDocsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="relative flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black text-xs px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-folder-line text-sm"></i>
                <span className="hidden sm:inline">Docs</span>
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FDB913] text-black text-[10px] font-black rounded-full flex items-center justify-center leading-none">
                  {unreadAttorneyDocCount}
                </span>
              </button>
            )}

            {unreadNotificationCount > 0 && (
              <button
                onClick={() => notificationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="relative flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black text-xs px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-notification-3-line text-sm"></i>
                <span className="hidden sm:inline">Alerts</span>
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FDB913] text-black text-[10px] font-black rounded-full flex items-center justify-center leading-none">
                  {unreadNotificationCount}
                </span>
              </button>
            )}

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-lg transition-all border border-white/20 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line"></i>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-black text-white mb-2">
            Welcome back, {client?.name || 'Client'}
          </h2>
          <p className="text-white/60 text-base">
            {client?.company && `${client.company} • `}{client?.email}
          </p>
        </div>

        {/* Just-authorized live notification */}
        {justAuthorized && (
          <div className="mb-6 bg-gradient-to-r from-green-500/30 to-green-500/10 border-2 border-green-500/60 rounded-xl p-5 flex items-center justify-between gap-4 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-green-500/30 rounded-xl border border-green-500/50 flex-shrink-0">
                <i className="ri-shield-check-fill text-green-400 text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-base">Your Account Has Been Authorized!</p>
                <p className="text-white/60 text-xs mt-0.5">
                  Your attorney just approved your account. You can now review and sign your Engagement Letter below.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => { setShowEngagementModal(true); setJustAuthorized(false); }}
                className="bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm px-5 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line mr-2"></i>Sign Now
              </button>
              <button onClick={() => setJustAuthorized(false)} className="p-2 text-white/40 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Pending authorization */}
        {!isAuthorized && (
          <div className="mb-6 bg-white/5 border border-white/10 rounded-xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
              <i className="ri-time-line text-white/40 text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm">Account Pending Authorization</p>
              <p className="text-white/50 text-xs mt-0.5">Your account is being reviewed. Once authorized, you will be able to access and sign your Engagement Letter.</p>
            </div>
          </div>
        )}

        {/* ── Pinned IOR Profile Incomplete Banner ── */}
        {client && !isProfileComplete && (
          <div className="mb-6 bg-gradient-to-r from-[#FDB913]/20 to-amber-500/10 border-2 border-[#FDB913]/70 rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#FDB913]/25 border border-[#FDB913]/50 rounded-xl flex-shrink-0">
                <i className="ri-user-settings-fill text-[#FDB913] text-xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-sm">Action Required: Complete Your IOR Profile</p>
                <p className="text-white/60 text-xs mt-0.5">
                  Your Importer of Record name, address &amp; phone are required before your case can be filed with CBP. Please fill in the form below.
                </p>
              </div>
            </div>
            <button
              onClick={() => document.getElementById('ior-profile-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              className="bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-xs px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-arrow-down-line mr-1"></i>Complete Now
            </button>
          </div>
        )}

        {/* Sign Now banner */}
        {needsSignature && (
          <div className="mb-6 bg-gradient-to-r from-[#FDB913]/20 to-[#FDB913]/10 border-2 border-[#FDB913]/60 rounded-xl p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/30 rounded-xl border border-[#FDB913]/50 flex-shrink-0">
                <i className="ri-quill-pen-fill text-[#FDB913] text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-base">Action Required: Sign Your Engagement Letter</p>
                <p className="text-white/60 text-xs mt-0.5">
                  Your account has been authorized. Please review and sign your Engagement Letter to proceed.
                  Compliant with ESIGN Act (15 U.S.C. § 7001) &amp; UETA.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowEngagementModal(true)}
              className="bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm px-5 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-file-text-line mr-2"></i>Sign Now
            </button>
          </div>
        )}

        {/* Signed confirmation */}
        {isSigned && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
            <div className="flex-1 min-w-0">
              <p className="text-green-400 font-bold text-sm">Engagement Letter Signed</p>
              <p className="text-white/40 text-xs">
                Signed on {new Date(extClient.engagement_signed_at!).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            {extClient.engagement_letter_url && (
              <a
                href={extClient.engagement_letter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-green-400 hover:text-green-300 text-xs font-bold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-line"></i>Download Copy
              </a>
            )}
          </div>
        )}

        {/* Post-signing: Fund Retainer Next Step Banner */}
        {isSigned && showFundRetainerPrompt && (retainer?.balance || 0) === 0 && (
          <div className="mb-6 bg-gradient-to-r from-[#FDB913]/25 to-[#FDB913]/10 border-2 border-[#FDB913]/60 rounded-xl p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/30 rounded-xl border border-[#FDB913]/50 flex-shrink-0">
                <i className="ri-money-dollar-circle-fill text-[#FDB913] text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-base">Next Step: Fund Your Retainer</p>
                <p className="text-white/60 text-xs mt-0.5">
                  Your Engagement Letter is signed! Funding your retainer is the next required step to keep your case moving.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  fundRetainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setShowFundRetainerPrompt(false);
                }}
                className="bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm px-5 py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-line mr-2"></i>Fund Now
              </button>
              <button onClick={() => setShowFundRetainerPrompt(false)} className="p-2 text-white/40 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* 1. Fund Your Retainer */}
            <div ref={fundRetainerRef} className="bg-gradient-to-br from-[#FDB913]/20 to-[#FDB913]/10 border-2 border-[#FDB913]/60 rounded-2xl p-6">
              <div className="text-center mb-4">
                <i className="ri-money-dollar-circle-fill text-[#FDB913] text-5xl mb-3"></i>
                <h3 className="text-xl font-black text-white mb-2">Fund Your Retainer</h3>
                <p className="text-white/70 text-sm">Secure your legal representation by funding your retainer account</p>
              </div>
              <button
                onClick={handleFundRetainer}
                className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-line text-xl"></i>Fund Retainer
              </button>
            </div>

            {isLowBalance && (
              <div className="bg-gradient-to-br from-red-500/20 to-red-500/10 border-2 border-red-500/60 rounded-2xl p-6">
                <div className="text-center mb-4">
                  <i className="ri-alarm-warning-fill text-red-400 text-5xl mb-3"></i>
                  <h3 className="text-xl font-black text-white mb-2">Replenish Retainer</h3>
                  <p className="text-white/70 text-sm">Your balance is running low. Add funds to continue services</p>
                </div>
                <button
                  onClick={handleFundRetainer}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-black text-base py-3 rounded-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line text-xl"></i>Replenish Now
                </button>
              </div>
            )}

            {/* 2. Retainer Balance — reduced 50% */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-black text-white">Retainer Balance</h3>
                <div className={`${balance > 0 ? 'bg-[#FDB913]/20 border-[#FDB913]/40' : 'bg-white/10 border-white/20'} border rounded-full px-3 py-0.5`}>
                  <span className={`${balance > 0 ? 'text-[#FDB913]' : 'text-white/60'} text-xs font-black tracking-widest uppercase`}>
                    {balance > 0 ? 'Active' : 'Unfunded'}
                  </span>
                </div>
              </div>
              <div className="text-center py-3">
                <p className="text-white/60 text-xs mb-1">Current Balance</p>
                <p className="text-3xl font-black text-[#FDB913] mb-3">{formatCurrency(balance)}</p>
                {balance === 0 && <p className="text-white/40 text-xs">No retainer funded yet</p>}
                {isLowBalance && (
                  <div className="inline-flex items-center gap-1.5 bg-red-500/20 border border-red-500/40 rounded-lg px-3 py-1.5">
                    <i className="ri-alert-fill text-red-400 text-sm"></i>
                    <p className="text-red-400 text-xs font-bold">Low Balance — Consider Replenishing</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-white/5 border border-white/10 rounded-lg p-2.5 text-center">
                  <p className="text-white/60 text-xs mb-0.5">Total Funded</p>
                  <p className="text-base font-black text-white">{formatCurrency(totalFunded)}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-2.5 text-center">
                  <p className="text-white/60 text-xs mb-0.5">Total Used</p>
                  <p className="text-base font-black text-white">{formatCurrency(totalUsed)}</p>
                </div>
              </div>
            </div>

            {/* 3. Complete Your Profile */}
            {client && !isProfileComplete && (
              <div id="ior-profile-section">
                <ProfileCompletion
                  clientId={client.id}
                  initialName={extClient?.importer_of_record_name}
                  initialAddress={extClient?.importer_of_record_address}
                  initialPhone={extClient?.importer_of_record_phone}
                  initialLiquidationDate={extClient?.liquidation_date}
                  initialProtestFileDate={extClient?.protest_file_date}
                  initialClientNotes={extClient?.client_notes}
                  onCompleted={(data) => {
                    setClient(prev => prev ? {
                      ...prev,
                      importer_of_record_name: data.name,
                      importer_of_record_address: data.address,
                      importer_of_record_phone: data.phone,
                    } as ExtendedClient : prev);
                  }}
                />
              </div>
            )}

            {/* Profile complete confirmation */}
            {client && isProfileComplete && (
              <div className="mb-0 bg-green-500/8 border border-green-500/25 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-400 text-lg flex-shrink-0"></i>
                <div className="flex-1 min-w-0">
                  <p className="text-green-400 font-bold text-sm">Importer of Record Profile Complete</p>
                  <p className="text-white/40 text-xs truncate mt-0.5">
                    {extClient.importer_of_record_name} &bull; {extClient.importer_of_record_phone}
                  </p>
                </div>
                <button
                  onClick={() => setClient(prev => prev ? { ...prev, importer_of_record_name: null } as ExtendedClient : prev)}
                  className="text-white/25 hover:text-white/50 text-xs cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
                  title="Edit profile info"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
              </div>
            )}

            {/* 4. Estimated Tariff Refund */}
            <TariffRefundEstimator
              clientId={client?.id || ''}
              clientName={client?.name || ''}
              eligibilityAuthorized={eligibilityAuthorized}
            />

            {/* 5. CBP Form 5291 */}
            <Form5291
              clientId={client?.id || ''}
              userId={userId}
              clientName={client?.name || ''}
              clientEmail={client?.email || ''}
              company={client?.company}
              phone={client?.phone}
              alreadySubmitted={!!form5291SubmittedAt}
              onSubmitted={handleForm5291Submitted}
              importerName={extClient?.importer_of_record_name}
              importerAddress={extClient?.importer_of_record_address}
              importerPhone={extClient?.importer_of_record_phone}
            />

            {/* 6. CBP Form 4811 */}
            <div ref={form5297Ref}>
              <Form4811
                clientId={client?.id || ''}
                clientName={client?.name || ''}
                clientEmail={client?.email || ''}
                company={client?.company}
                alreadySubmitted={!!form4811SubmittedAt}
                onSubmitted={handleForm4811Submitted}
                importerName={extClient?.importer_of_record_name}
                importerAddress={extClient?.importer_of_record_address}
                importerPhone={extClient?.importer_of_record_phone}
              />
            </div>

            {/* 7. Required Documents for Refund Claim */}
            <div ref={cbpDocsRef}>
              <CBPDocumentGathering
                clientId={client?.id || ''}
                userId={userId}
                alreadySubmitted={!!cbpDocsSubmittedAt}
                onAllSubmitted={(submittedAt) => setCbpDocsSubmittedAt(submittedAt)}
              />
            </div>

            {/* 8. Case Documents */}
            <DocumentUpload
              clientId={client?.id || ''}
              userId={userId}
              documents={documents}
              onDocumentAdded={(doc) => setDocuments(prev => [doc, ...prev])}
            />

            {/* 9. Pre-Filing Readiness */}
            <PreFilingChecklist
              isAuthorized={extClient?.admin_authorized === true}
              engagementSigned={!!extClient?.engagement_signed_at}
              form5291Submitted={!!form5291SubmittedAt}
              form5297Submitted={!!form5297SubmittedAt}
              cbpDocsSubmitted={!!cbpDocsSubmittedAt}
              retainerFunded={(retainer?.balance || 0) > 0}
              onGoToForm5291={() => { const el = document.getElementById(`cbp-form-5291-${client?.id}`); el?.closest('.rounded-2xl')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              onGoToForm5297={() => form5297Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              onGoToCBPDocs={() => cbpDocsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              onGoToFundRetainer={() => fundRetainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            />

            {/* 10. Case Status */}
            <CaseStatusTracker caseStatus={caseStatus} eligibilityAuthorized={eligibilityAuthorized} />

            {/* 11. Messages from Attorney */}
            <div ref={messagesRef}>
              <ClientMessages
                clientId={client?.id || ''}
                preferredLanguage={preferredLanguage}
                onUnreadCountChange={setUnreadMessageCount}
              />
            </div>

            {/* 12. Case Notes */}
            <CaseNotes clientId={client?.id || ''} notes={caseNotes} preferredLanguage={preferredLanguage} />

            {/* 13. Notifications */}
            <div ref={notificationsRef}>
              <NotificationsPanel clientId={client?.id || ''} onUnreadCountChange={setUnreadNotificationCount} />
            </div>

            {/* 14. Retainer Statement */}
            <RetainerStatement
              transactions={transactions}
              retainer={retainer}
              onFundRetainer={handleFundRetainer}
              clientInfo={{ name: client?.name || '', email: client?.email || '', company: client?.company }}
            />

            {/* 15. Book a Consult */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 border-2 border-white/20 hover:border-[#FDB913]/50 rounded-2xl p-6 transition-all">
              <div className="text-center mb-4">
                <div className="w-14 h-14 flex items-center justify-center bg-[#FDB913]/20 rounded-xl border border-[#FDB913]/40 mx-auto mb-3">
                  <i className="ri-calendar-event-fill text-[#FDB913] text-3xl"></i>
                </div>
                <h3 className="text-xl font-black text-white mb-2">Book a Consultation</h3>
                <p className="text-white/60 text-sm">Schedule a call with Attorney Thabit to discuss your case</p>
              </div>
              <button
                onClick={() => navigate('/book')}
                className="w-full bg-white text-black font-black text-base py-3 rounded-lg transition-all duration-300 hover:bg-[#FDB913] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-2-line text-xl"></i>Schedule Now
              </button>
            </div>

            {/* 17. My Appointments */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
              <h3 className="text-2xl font-black text-white mb-6">My Appointments</h3>
              {appointmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-10 h-10 border-4 border-[#FDB913] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-6">
                    {(['upcoming', 'past'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setAppointmentsTab(tab)}
                        className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                          appointmentsTab === tab ? 'bg-[#FDB913] text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        {tab === 'upcoming' ? `Upcoming (${upcomingAppointments.length})` : `Past (${pastAppointments.length})`}
                      </button>
                    ))}
                  </div>

                  {appointmentsTab === 'upcoming' && (
                    <div className="space-y-4">
                      {upcomingAppointments.length === 0 ? (
                        <div className="text-center py-12">
                          <i className="ri-calendar-line text-white/20 text-6xl mb-4"></i>
                          <p className="text-white/40 text-base">No upcoming appointments</p>
                          <button
                            onClick={() => navigate('/book')}
                            className="mt-4 inline-flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/40 text-[#FDB913] font-bold text-sm px-5 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-calendar-event-line"></i>Book a Consultation
                          </button>
                        </div>
                      ) : upcomingAppointments.map((apt) => (
                        <div key={apt.id} className="bg-gradient-to-br from-[#FDB913]/20 to-[#FDB913]/10 border-2 border-[#FDB913]/60 rounded-xl p-5">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/30 rounded-xl border border-[#FDB913]/50 flex-shrink-0">
                                <i className="ri-calendar-event-fill text-[#FDB913] text-xl"></i>
                              </div>
                              <div>
                                <p className="text-white font-black text-lg mb-1">{formatAppointmentDate(apt.start_time)}</p>
                                <p className="text-[#FDB913] font-bold text-base">{formatAppointmentTime(apt.start_time)} – {formatAppointmentTime(apt.end_time)}</p>
                              </div>
                            </div>
                            <div className={`inline-flex items-center gap-1 ${getStatusBadge(apt.status)} border rounded-full px-3 py-1`}>
                              <span className="text-xs font-bold uppercase">{apt.status}</span>
                            </div>
                          </div>
                          {apt.notes && (
                            <div className="bg-black/20 border border-white/10 rounded-lg p-3 mb-3">
                              <p className="text-white/70 text-sm">{apt.notes}</p>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-white/60 text-xs">
                            <i className="ri-information-line"></i>
                            <p>Attorney Thabit or a team member will call you at the scheduled time</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {appointmentsTab === 'past' && (
                    <div className="space-y-3">
                      {pastAppointments.length === 0 ? (
                        <div className="text-center py-12">
                          <i className="ri-history-line text-white/20 text-6xl mb-4"></i>
                          <p className="text-white/40 text-base">No past appointments</p>
                        </div>
                      ) : pastAppointments.map((apt) => (
                        <div key={apt.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-full flex-shrink-0">
                                <i className="ri-calendar-check-line text-white/60 text-lg"></i>
                              </div>
                              <div>
                                <p className="text-white font-bold text-sm">{formatAppointmentDate(apt.start_time)}</p>
                                <p className="text-white/50 text-xs">{formatAppointmentTime(apt.start_time)} – {formatAppointmentTime(apt.end_time)}</p>
                              </div>
                            </div>
                            <div className={`inline-flex items-center gap-1 ${getStatusBadge(apt.status)} border rounded-full px-2 py-0.5`}>
                              <span className="text-xs font-bold uppercase">{apt.status}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {isAuthorized && (
              <div className={`border-2 rounded-2xl p-5 ${isSigned ? 'bg-green-500/10 border-green-500/40' : 'bg-[#FDB913]/10 border-[#FDB913]/60'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${isSigned ? 'bg-green-500/20' : 'bg-[#FDB913]/20'}`}>
                    <i className={`${isSigned ? 'ri-checkbox-circle-fill text-green-400' : 'ri-quill-pen-fill text-[#FDB913]'} text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-white font-black text-sm">Engagement Letter</p>
                    <p className={`text-xs ${isSigned ? 'text-green-400' : 'text-[#FDB913]'}`}>
                      {isSigned ? 'Signed & Locked' : 'Signature Required'}
                    </p>
                  </div>
                </div>
                {!isSigned ? (
                  <>
                    <div className="mb-3">
                      <label className="block text-[#FDB913]/80 text-xs font-bold mb-1.5 uppercase tracking-wide">
                        Importer of Record Company Name
                      </label>
                      <input
                        type="text"
                        value={importerOfRecordPrefill}
                        onChange={(e) => setImporterOfRecordPrefill(e.target.value)}
                        placeholder={client?.company || 'Your legal entity / company name'}
                        className="w-full px-3 py-2 bg-black/40 border border-[#FDB913]/30 rounded-lg text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#FDB913]/60 transition-colors"
                      />
                      <p className="text-white/30 text-xs mt-1">Pre-fills the signature form</p>
                    </div>
                    <button
                      onClick={() => setShowEngagementModal(true)}
                      className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm py-3 rounded-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-quill-pen-line"></i>Sign Now
                    </button>
                  </>
                ) : extClient?.engagement_letter_url && (
                  <a
                    href={extClient.engagement_letter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-bold text-sm py-2.5 rounded-lg transition-all whitespace-nowrap cursor-pointer border border-green-500/30"
                  >
                    <i className="ri-download-line"></i>Download Signed PDF
                  </a>
                )}
              </div>
            )}

            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <i className="ri-shield-keyhole-fill text-[#FDB913] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-white/60 text-xs leading-relaxed">
                  <strong className="text-white/80">Secure Portal.</strong> All transactions are encrypted and protected by 256-bit SSL.
                </p>
              </div>
            </div>
          </div>

          {/* Attorney Documents Section */}
          <div ref={attorneyDocsRef}>
            {client?.id && (
              <AttorneyDocuments
                clientId={client.id}
                onUnreadCountChange={setUnreadAttorneyDocCount}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
